import React, { useState, useMemo } from 'react';
import {
  Card, Row, Col, Table, Badge, Button, Form, Spinner, Alert,
  Modal,
} from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_PURCHASES,
  CREATE_PURCHASE,
  UPDATE_PURCHASE,
  UPDATE_PURCHASE_STATUS,
  DELETE_PURCHASE,
} from '../../graphql/operations';
import * as XLSX from 'xlsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PurchaseItem {
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
}

interface Purchase {
  id: string;
  purchaseId: string;
  supplierName: string;
  supplierContact?: string;
  supplierGSTIN?: string;
  category?: string;
  items: PurchaseItem[];
  totalAmount: number;
  paidAmount?: number;
  balanceDue?: number;
  paymentStatus?: string;
  paymentMode?: string;
  invoiceRef?: string;
  date: string;
  notes?: string;
  createdAt?: string;
}

const CATEGORIES = [
  'Raw Materials', 'Printing Ink', 'Paper & Media', 'Machinery Parts',
  'Office Supplies', 'Packaging', 'Chemicals', 'Equipment', 'General',
];

const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'Credit', 'Other'];
const STATUSES = ['Ordered', 'Received', 'Partially Received', 'Cancelled'];

const EMPTY_ITEM: PurchaseItem = { name: '', quantity: 1, unit: 'pcs', unitPrice: 0, totalPrice: 0 };

const statusBadgeVariant = (status?: string) => {
  switch (status) {
    case 'Received': return 'success';
    case 'Partially Received': return 'warning';
    case 'Cancelled': return 'danger';
    case 'Ordered': return 'info';
    default: return 'secondary';
  }
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Empty form state ─────────────────────────────────────────────────────────
const emptyForm = () => ({
  supplierName: '',
  supplierContact: '',
  supplierGSTIN: '',
  category: 'General',
  date: new Date().toISOString().slice(0, 10),
  paymentStatus: 'Ordered',
  paymentMode: '',
  invoiceRef: '',
  paidAmount: 0,
  notes: '',
  items: [{ ...EMPTY_ITEM }],
});

// ─── Main Component ───────────────────────────────────────────────────────────
const PurchaseTracker: React.FC = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusPurchase, setStatusPurchase] = useState<Purchase | null>(null);
  const [quickStatus, setQuickStatus] = useState('');
  const [quickPayMode, setQuickPayMode] = useState('');
  const [quickPaid, setQuickPaid] = useState(0);

  const { data, loading, error, refetch } = useQuery(GET_PURCHASES, {
    fetchPolicy: 'network-only',
  });

  const [createPurchase] = useMutation(CREATE_PURCHASE, {
    refetchQueries: [{ query: GET_PURCHASES }],
  });
  const [updatePurchase] = useMutation(UPDATE_PURCHASE, {
    refetchQueries: [{ query: GET_PURCHASES }],
  });
  const [updatePurchaseStatus] = useMutation(UPDATE_PURCHASE_STATUS, {
    refetchQueries: [{ query: GET_PURCHASES }],
  });
  const [deletePurchase] = useMutation(DELETE_PURCHASE, {
    refetchQueries: [{ query: GET_PURCHASES }],
  });

  const allPurchases: Purchase[] = data?.purchases || [];

  // ─── Filtering ───────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return allPurchases.filter(p => {
      const q = search.toLowerCase();
      const matchSearch = !q || p.supplierName.toLowerCase().includes(q) ||
        p.purchaseId.toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.invoiceRef || '').toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || p.paymentStatus === statusFilter;
      const matchCategory = categoryFilter === 'all' || p.category === categoryFilter;
      const matchFrom = !dateFrom || p.date >= dateFrom;
      const matchTo = !dateTo || p.date <= dateTo;
      return matchSearch && matchStatus && matchCategory && matchFrom && matchTo;
    });
  }, [allPurchases, search, statusFilter, categoryFilter, dateFrom, dateTo]);

  // ─── Summary Metrics ─────────────────────────────────────────────────────────
  const metrics = useMemo(() => {
    const now = new Date();
    const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const thisMonth = allPurchases.filter(p => p.date >= firstOfMonth);

    const total = allPurchases.reduce((s, p) => s + (p.totalAmount || 0), 0);
    const paid = allPurchases.reduce((s, p) => s + (p.paidAmount || 0), 0);
    const balance = allPurchases.reduce((s, p) => s + (p.balanceDue || 0), 0);
    const monthTotal = thisMonth.reduce((s, p) => s + (p.totalAmount || 0), 0);
    const pending = allPurchases.filter(p => p.paymentStatus === 'Ordered').length;
    const received = allPurchases.filter(p => p.paymentStatus === 'Received').length;
    return { total, paid, balance, monthTotal, pending, received, count: allPurchases.length };
  }, [allPurchases]);

  // ─── Form helpers ─────────────────────────────────────────────────────────────
  const recalcItem = (items: PurchaseItem[]) =>
    items.map(it => ({ ...it, totalPrice: it.quantity * it.unitPrice }));

  const totalFromItems = (items: PurchaseItem[]) =>
    items.reduce((s, it) => s + it.totalPrice, 0);

  const setFormField = (key: string, val: any) =>
    setForm(prev => ({ ...prev, [key]: val }));

  const setItemField = (idx: number, key: keyof PurchaseItem, val: any) => {
    const items = form.items.map((it, i) => i === idx ? { ...it, [key]: val } : it);
    const recalced = recalcItem(items);
    setForm(prev => ({ ...prev, items: recalced }));
  };

  const addItem = () =>
    setForm(prev => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM }] }));

  const removeItem = (idx: number) =>
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const openAddModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (p: Purchase) => {
    setIsEditing(true);
    setEditingId(p.id);
    setForm({
      supplierName: p.supplierName,
      supplierContact: p.supplierContact || '',
      supplierGSTIN: p.supplierGSTIN || '',
      category: p.category || 'General',
      date: p.date,
      paymentStatus: p.paymentStatus || 'Ordered',
      paymentMode: p.paymentMode || '',
      invoiceRef: p.invoiceRef || '',
      paidAmount: p.paidAmount || 0,
      notes: p.notes || '',
      items: p.items.length ? p.items.map(it => ({ ...it })) : [{ ...EMPTY_ITEM }],
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.supplierName.trim()) { setFormError('Supplier name is required.'); return; }
    if (!form.date) { setFormError('Date is required.'); return; }
    if (form.items.length === 0 || form.items.every(it => !it.name.trim())) {
      setFormError('Add at least one item.'); return;
    }
    setFormError('');
    setSaving(true);
    const validItems = form.items.filter(it => it.name.trim());
    const totalAmount = totalFromItems(validItems);
    const paidAmount = Number(form.paidAmount) || 0;
    const input = {
      supplierName: form.supplierName.trim(),
      supplierContact: form.supplierContact.trim(),
      supplierGSTIN: form.supplierGSTIN.trim(),
      category: form.category,
      date: form.date,
      paymentStatus: form.paymentStatus,
      paymentMode: form.paymentMode,
      invoiceRef: form.invoiceRef.trim(),
      paidAmount,
      balanceDue: totalAmount - paidAmount,
      notes: form.notes.trim(),
      items: validItems,
      totalAmount,
    };
    try {
      if (isEditing && editingId) {
        const { data: d } = await updatePurchase({ variables: { id: editingId, input } });
        if (!d?.updatePurchase?.success) throw new Error(d?.updatePurchase?.message || 'Update failed');
      } else {
        const { data: d } = await createPurchase({ variables: { input } });
        if (!d?.createPurchase?.success) throw new Error(d?.createPurchase?.message || 'Create failed');
      }
      setShowModal(false);
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    setConfirmDeleteId(null);
    setDeleting(true);
    try {
      await deletePurchase({ variables: { id } });
    } catch { }
    setDeleting(false);
  };

  const openStatusModal = (p: Purchase) => {
    setStatusPurchase(p);
    setQuickStatus(p.paymentStatus || 'Ordered');
    setQuickPayMode(p.paymentMode || '');
    setQuickPaid(p.paidAmount || 0);
    setShowStatusModal(true);
  };

  const handleStatusSave = async () => {
    if (!statusPurchase) return;
    try {
      await updatePurchaseStatus({
        variables: {
          id: statusPurchase.id,
          paymentStatus: quickStatus,
          paymentMode: quickPayMode || null,
          paidAmount: Number(quickPaid),
        },
      });
      setShowStatusModal(false);
    } catch { }
  };

  const handleExportExcel = () => {
    const rows = filtered.map(p => ({
      'Purchase ID': p.purchaseId,
      'Date': p.date,
      'Supplier': p.supplierName,
      'Category': p.category || '',
      'Status': p.paymentStatus || '',
      'Total (₹)': p.totalAmount,
      'Paid (₹)': p.paidAmount || 0,
      'Balance (₹)': p.balanceDue || 0,
      'Payment Mode': p.paymentMode || '',
      'Invoice Ref': p.invoiceRef || '',
      'Notes': p.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Purchases');
    XLSX.writeFile(wb, `purchases_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="purchase-tracker">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <h4 className="fw-bold text-dark mb-1">Purchase Management</h4>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            Track supplier purchases, stock procurement, and payment status.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-success"
            size="sm"
            onClick={handleExportExcel}
            style={{ borderRadius: 8, fontSize: 13 }}
          >
            <i className="fas fa-file-excel me-1" /> Export Excel
          </Button>
          <Button
            variant="primary"
            onClick={openAddModal}
            style={{ borderRadius: 8, fontSize: 13, background: 'linear-gradient(135deg, #e91e8c, #3b5bdb)', border: 'none' }}
          >
            <i className="fas fa-plus me-2" />Record Purchase
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="g-3 mb-4">
        {[
          { label: 'Total Purchases', value: `₹${metrics.total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, icon: 'fas fa-shopping-bag', color: '#6366f1', sub: `${metrics.count} records` },
          { label: 'Total Paid', value: `₹${metrics.paid.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, icon: 'fas fa-check-circle', color: '#10b981', sub: `${metrics.received} received` },
          { label: 'Balance Due', value: `₹${metrics.balance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, icon: 'fas fa-hourglass-half', color: '#f59e0b', sub: `${metrics.pending} pending orders` },
          { label: 'This Month', value: `₹${metrics.monthTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, icon: 'fas fa-calendar-alt', color: '#0ea5e9', sub: 'Current month spend' },
        ].map((card, i) => (
          <Col md={3} sm={6} key={i}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 14, overflow: 'hidden', position: 'relative' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', backgroundColor: card.color }} />
              <Card.Body className="p-3 ps-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted fw-semibold" style={{ fontSize: 11, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{card.label}</span>
                  <div className="d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: '50%', background: `${card.color}18` }}>
                    <i className={card.icon} style={{ color: card.color, fontSize: 15 }} />
                  </div>
                </div>
                <h5 className="fw-bold mb-0">{card.value}</h5>
                <small className="text-muted">{card.sub}</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
        <Card.Body className="p-3">
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Search supplier, ID, category…"
                size="sm"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ borderRadius: 8, fontSize: 13 }}
              />
            </Col>
            <Col md={2}>
              <Form.Select size="sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ borderRadius: 8, fontSize: 13 }}>
                <option value="all">All Statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select size="sm" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ borderRadius: 8, fontSize: 13 }}>
                <option value="all">All Categories</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Control type="date" size="sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ borderRadius: 8, fontSize: 13 }} />
            </Col>
            <Col md={2}>
              <Form.Control type="date" size="sm" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ borderRadius: 8, fontSize: 13 }} />
            </Col>
            <Col md={1}>
              <Button size="sm" variant="outline-secondary" style={{ borderRadius: 8, fontSize: 13, width: '100%' }} onClick={() => { setSearch(''); setStatusFilter('all'); setCategoryFilter('all'); setDateFrom(''); setDateTo(''); }}>
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : error ? (
        <Alert variant="danger">Failed to load purchases: {error.message}</Alert>
      ) : filtered.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-shopping-bag fs-1 mb-3 d-block" style={{ color: '#cbd5e1' }} />
          <p className="mb-1 fw-semibold">No purchases found</p>
          <small>Click "Record Purchase" to add your first purchase entry.</small>
        </div>
      ) : (
        <Card className="border-0 shadow-sm" style={{ borderRadius: 14 }}>
          <div className="table-responsive">
            <Table hover className="align-middle mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold">Purchase ID</th>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold">Date</th>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold">Supplier</th>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold">Category</th>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold">Items</th>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold">Total</th>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold">Paid</th>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold">Balance</th>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold">Status</th>
                  <th className="py-3 px-3 border-0 text-muted fw-semibold text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td className="py-3 px-3">
                      <span className="fw-bold" style={{ color: '#6366f1' }}>{p.purchaseId}</span>
                    </td>
                    <td className="py-3 px-3 text-muted">{formatDate(p.date)}</td>
                    <td className="py-3 px-3">
                      <div className="fw-semibold text-dark">{p.supplierName}</div>
                      {p.supplierContact && <small className="text-muted">{p.supplierContact}</small>}
                    </td>
                    <td className="py-3 px-3">
                      <span className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#475569', fontSize: 11 }}>
                        {p.category || '—'}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span className="badge bg-light text-dark" style={{ fontSize: 11 }}>{p.items.length} item{p.items.length !== 1 ? 's' : ''}</span>
                    </td>
                    <td className="py-3 px-3 fw-bold">₹{(p.totalAmount || 0).toLocaleString('en-IN')}</td>
                    <td className="py-3 px-3 text-success fw-semibold">₹{(p.paidAmount || 0).toLocaleString('en-IN')}</td>
                    <td className="py-3 px-3">
                      {(p.balanceDue || 0) > 0 ? (
                        <span className="text-danger fw-semibold">₹{(p.balanceDue || 0).toLocaleString('en-IN')}</span>
                      ) : (
                        <span className="text-success">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <Badge
                        bg={statusBadgeVariant(p.paymentStatus)}
                        text={p.paymentStatus === 'Partially Received' ? 'dark' : undefined}
                        className="fw-normal"
                        style={{ fontSize: 11, cursor: 'pointer', letterSpacing: '0.3px' }}
                        onClick={() => openStatusModal(p)}
                        title="Click to update status"
                      >
                        {p.paymentStatus || 'Ordered'}
                      </Badge>
                    </td>
                    <td className="py-3 px-3 text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          style={{ borderRadius: 6, padding: '3px 8px' }}
                          onClick={() => openEditModal(p)}
                          title="Edit"
                        >
                          <i className="fas fa-edit" />
                        </Button>
                        <Button
                          variant={confirmDeleteId === p.id ? 'danger' : 'outline-danger'}
                          size="sm"
                          style={{ borderRadius: 6, padding: '3px 8px' }}
                          onClick={() => handleDelete(p.id)}
                          disabled={deleting}
                          title={confirmDeleteId === p.id ? 'Click again to confirm delete' : 'Delete'}
                        >
                          {deleting && confirmDeleteId === p.id ? <Spinner size="sm" animation="border" /> : <i className="fas fa-trash" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="px-3 py-2 border-top" style={{ background: '#f8fafc', borderRadius: '0 0 14px 14px' }}>
            <small className="text-muted">
              Showing {filtered.length} of {allPurchases.length} purchase{allPurchases.length !== 1 ? 's' : ''}
              &nbsp;·&nbsp; Total: <strong>₹{filtered.reduce((s, p) => s + p.totalAmount, 0).toLocaleString('en-IN')}</strong>
            </small>
          </div>
        </Card>
      )}

      {/* ─── Add / Edit Purchase Modal ─── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header
          closeButton
          style={{ background: 'linear-gradient(135deg, #6366f1 0%, #3b5bdb 100%)', color: '#fff', border: 'none' }}
        >
          <Modal.Title style={{ fontSize: 17, fontWeight: 700 }}>
            <i className="fas fa-shopping-bag me-2" />
            {isEditing ? 'Edit Purchase' : 'Record New Purchase'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {formError && <Alert variant="danger" className="py-2 mb-3">{formError}</Alert>}

          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Supplier Name *</Form.Label>
              <Form.Control
                size="sm"
                value={form.supplierName}
                onChange={e => setFormField('supplierName', e.target.value)}
                placeholder="e.g. ABC Suppliers"
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Supplier Contact</Form.Label>
              <Form.Control
                size="sm"
                value={form.supplierContact}
                onChange={e => setFormField('supplierContact', e.target.value)}
                placeholder="Phone / Email"
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Supplier GSTIN</Form.Label>
              <Form.Control
                size="sm"
                value={form.supplierGSTIN}
                onChange={e => setFormField('supplierGSTIN', e.target.value)}
                placeholder="GST Number"
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Category</Form.Label>
              <Form.Select size="sm" value={form.category} onChange={e => setFormField('category', e.target.value)} style={{ borderRadius: 8 }}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Date *</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={form.date}
                onChange={e => setFormField('date', e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </Col>
          </Row>

          {/* Items */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold text-dark" style={{ fontSize: 13 }}>Purchase Items</span>
              <Button variant="outline-primary" size="sm" style={{ borderRadius: 7, fontSize: 12 }} onClick={addItem}>
                <i className="fas fa-plus me-1" /> Add Item
              </Button>
            </div>
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
              {/* Header */}
              <Row className="g-1 mb-1 text-muted" style={{ fontSize: 11, fontWeight: 600 }}>
                <Col xs={4}>ITEM NAME</Col>
                <Col xs={2}>QTY</Col>
                <Col xs={2}>UNIT</Col>
                <Col xs={2}>UNIT PRICE (₹)</Col>
                <Col xs={1} className="text-end">TOTAL</Col>
                <Col xs={1} />
              </Row>
              {form.items.map((item, idx) => (
                <Row className="g-1 mb-2 align-items-center" key={idx}>
                  <Col xs={4}>
                    <Form.Control
                      size="sm"
                      placeholder="Item name"
                      value={item.name}
                      onChange={e => setItemField(idx, 'name', e.target.value)}
                      style={{ borderRadius: 7, fontSize: 12 }}
                    />
                  </Col>
                  <Col xs={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      value={item.quantity}
                      onChange={e => setItemField(idx, 'quantity', Number(e.target.value))}
                      style={{ borderRadius: 7, fontSize: 12 }}
                    />
                  </Col>
                  <Col xs={2}>
                    <Form.Control
                      size="sm"
                      placeholder="pcs"
                      value={item.unit}
                      onChange={e => setItemField(idx, 'unit', e.target.value)}
                      style={{ borderRadius: 7, fontSize: 12 }}
                    />
                  </Col>
                  <Col xs={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      value={item.unitPrice}
                      onChange={e => setItemField(idx, 'unitPrice', Number(e.target.value))}
                      style={{ borderRadius: 7, fontSize: 12 }}
                    />
                  </Col>
                  <Col xs={1} className="text-end">
                    <span className="fw-semibold text-dark" style={{ fontSize: 12 }}>
                      ₹{item.totalPrice.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </span>
                  </Col>
                  <Col xs={1} className="text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger p-0"
                      style={{ width: 26, height: 26, borderRadius: 6, fontSize: 11 }}
                      onClick={() => removeItem(idx)}
                      disabled={form.items.length === 1}
                    >
                      <i className="fas fa-times" />
                    </button>
                  </Col>
                </Row>
              ))}
              <div className="text-end mt-2 pt-2 border-top">
                <span className="text-muted me-2" style={{ fontSize: 12 }}>Grand Total:</span>
                <span className="fw-bold" style={{ fontSize: 15, color: '#6366f1' }}>
                  ₹{totalFromItems(form.items).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>
          </div>

          {/* Payment details */}
          <Row className="g-3">
            <Col md={4}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Payment Status</Form.Label>
              <Form.Select size="sm" value={form.paymentStatus} onChange={e => setFormField('paymentStatus', e.target.value)} style={{ borderRadius: 8 }}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Payment Mode</Form.Label>
              <Form.Select size="sm" value={form.paymentMode} onChange={e => setFormField('paymentMode', e.target.value)} style={{ borderRadius: 8 }}>
                <option value="">Select…</option>
                {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Amount Paid (₹)</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                min={0}
                value={form.paidAmount}
                onChange={e => setFormField('paidAmount', Number(e.target.value))}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Supplier Invoice / Bill Ref</Form.Label>
              <Form.Control
                size="sm"
                value={form.invoiceRef}
                onChange={e => setFormField('invoiceRef', e.target.value)}
                placeholder="Invoice / bill number"
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: 13 }}>Notes</Form.Label>
              <Form.Control
                size="sm"
                as="textarea"
                rows={1}
                value={form.notes}
                onChange={e => setFormField('notes', e.target.value)}
                placeholder="Internal notes…"
                style={{ borderRadius: 8 }}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top pt-2">
          <Button variant="outline-secondary" style={{ borderRadius: 8, fontSize: 13 }} onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{ borderRadius: 8, fontSize: 13, background: 'linear-gradient(135deg, #6366f1, #3b5bdb)', border: 'none' }}
          >
            {saving ? <><Spinner size="sm" animation="border" className="me-2" />Saving…</> : isEditing ? 'Save Changes' : 'Record Purchase'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Quick Status Modal ─── */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered size="sm">
        <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
          <Modal.Title style={{ fontSize: 15 }}>Update Status</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <div className="mb-2" style={{ fontSize: 12, color: '#64748b' }}>
            {statusPurchase?.purchaseId} — {statusPurchase?.supplierName}
          </div>
          <Form.Select size="sm" value={quickStatus} onChange={e => setQuickStatus(e.target.value)} className="mb-2" style={{ borderRadius: 8 }}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </Form.Select>
          <Form.Select size="sm" value={quickPayMode} onChange={e => setQuickPayMode(e.target.value)} className="mb-2" style={{ borderRadius: 8 }}>
            <option value="">Payment Mode</option>
            {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
          </Form.Select>
          <Form.Control
            size="sm"
            type="number"
            min={0}
            placeholder="Amount Paid (₹)"
            value={quickPaid}
            onChange={e => setQuickPaid(Number(e.target.value))}
            style={{ borderRadius: 8 }}
          />
        </Modal.Body>
        <Modal.Footer style={{ border: 'none', paddingTop: 0 }}>
          <Button variant="outline-secondary" size="sm" style={{ borderRadius: 8 }} onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button size="sm" style={{ borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #3b5bdb)', border: 'none' }} onClick={handleStatusSave}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PurchaseTracker;
